import { TenantThemeModeOverrides, TenantThemeOverrideMode } from 'src/app/base/services/theme.model';

export interface ThemeTokenOverrideRow {
  variable: string;
  mode: TenantThemeOverrideMode;
  value: string;
}

const modeOrder: Record<TenantThemeOverrideMode, number> = { light: 0, dark: 1 };

export function sortThemeTokenOverrideRows(rows: ThemeTokenOverrideRow[]): ThemeTokenOverrideRow[] {
  return [...rows].sort((left, right) => {
    const variableCompare = left.variable.localeCompare(right.variable);
    if (variableCompare !== 0) {
      return variableCompare;
    }

    return modeOrder[left.mode] - modeOrder[right.mode];
  });
}

export function buildRowsFromOverrides(overrides: TenantThemeModeOverrides | null | undefined): ThemeTokenOverrideRow[] {
  if (!overrides) {
    return [];
  }

  const rows: ThemeTokenOverrideRow[] = [];

  Object.entries(overrides.light ?? {}).forEach(([variable, value]) => {
    rows.push({ variable, mode: 'light', value });
  });
  Object.entries(overrides.dark ?? {}).forEach(([variable, value]) => {
    rows.push({ variable, mode: 'dark', value });
  });

  return sortThemeTokenOverrideRows(rows);
}

export function buildOverridesFromRows(rows: ThemeTokenOverrideRow[]): TenantThemeModeOverrides {
  const overrides: TenantThemeModeOverrides = {
    light: {},
    dark: {}
  };

  rows.forEach((row) => {
    if (row.mode === 'light') {
      overrides.light![row.variable] = row.value;
    } else {
      overrides.dark![row.variable] = row.value;
    }
  });

  return overrides;
}

export function buildRowsFromSnapshot(snapshot: ThemeTokenPreviewSnapshot): ThemeTokenOverrideRow[] {
  const rows: ThemeTokenOverrideRow[] = [];

  Object.entries(snapshot.light).forEach(([variable, value]) => {
    rows.push({ variable, mode: 'light', value });
  });
  Object.entries(snapshot.dark).forEach(([variable, value]) => {
    rows.push({ variable, mode: 'dark', value });
  });

  return sortThemeTokenOverrideRows(rows);
}

export interface ThemeTokenPreviewSnapshot {
  light: Record<string, string>;
  dark: Record<string, string>;
}

export function emptyThemeTokenPreviewSnapshot(): ThemeTokenPreviewSnapshot {
  return { light: {}, dark: {} };
}

export function snapshotFromOverrides(overrides: TenantThemeModeOverrides | null | undefined): ThemeTokenPreviewSnapshot {
  if (!overrides) {
    return emptyThemeTokenPreviewSnapshot();
  }

  return {
    light: { ...(overrides.light ?? {}) },
    dark: { ...(overrides.dark ?? {}) }
  };
}
